

// IndustryCards.jsx
import React, { useEffect, useRef, useState } from "react";
import industries from "./IndustriesData";
import CapabilitiesSection from "./CapabilitiesSection";
import { FaRegCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import icon1 from "../../Assets/common/manufacture.png";
import icon2 from "../../Assets/common/pharmaceutical-industry.png";
import icon3 from "../../Assets/common/retailer.png";
import icon4 from "../../Assets/common/autonomous-car.png";
import icon5 from "../../Assets/common/connection.png";
import icon6 from "../../Assets/common/restaurant.png";
import { useParams } from "react-router-dom";


const THEME = {
  manufacturing: {
    accent: "bg-blue-600",
    iconBg: "bg-blue-50",
    iconColor: "text-blue-700",
    statColor: "text-blue-700",
    badgeBg: "bg-blue-50",
    badgeText: "text-blue-800",
  },
  pharmaceuticals: {
    accent: "bg-pink-700",
    iconBg: "bg-pink-50",
    iconColor: "text-pink-700",
    statColor: "text-pink-700",
    badgeBg: "bg-pink-50",
    badgeText: "text-pink-900",
  },
  retail: {
    accent: "bg-teal-700",
    iconBg: "bg-teal-50",
    iconColor: "text-teal-700",
    statColor: "text-teal-700",
    badgeBg: "bg-teal-50",
    badgeText: "text-teal-900",
  },
  automotive: {
    accent: "bg-amber-600",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-700",
    statColor: "text-amber-700",
    badgeBg: "bg-amber-50",
    badgeText: "text-amber-900",
  },
  technology: {
    accent: "bg-indigo-600",
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-700",
    statColor: "text-indigo-700",
    badgeBg: "bg-indigo-50",
    badgeText: "text-indigo-900",
  },
  sustainability: {
    accent: "bg-green-700",
    iconBg: "bg-green-50",
    iconColor: "text-green-700",
    statColor: "text-green-700",
    badgeBg: "bg-green-50",
    badgeText: "text-green-900",
  },
};

const ICONS = {
  manufacturing: icon1,
  pharmaceuticals: icon2,
  retail: icon3,
  automotive: icon4,
  technology: icon5,
  sustainability: icon6,
};

function IndustryCard({ industry , highlight}) {
  const theme = THEME[industry.slug] ?? THEME.technology;
  const icon = ICONS[industry.slug];
  const navigate = useNavigate();

    const isHighlighted = highlight?.slug === industry.slug;

     const cardRef = useRef(null);

  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center", // start | center | end
      });
    }
  }, [isHighlighted]);


  return (
      <div
      ref={cardRef}
      className={`
        bg-white border rounded-xl overflow-hidden cursor-pointer
        transition-all duration-300
        hover:-translate-y-1 hover:shadow-lg

        ${
          isHighlighted
            ? "border-[#FEB501] shadow-2xl scale-[1.02]"
            : "border-gray-200"
        }
      `}
    >

      <div className="p-5">
        {/* Icon + stat */}
        <div className="flex items-center justify-between mb-6">
          <div
            className={`size-20 rounded-lg ${theme.iconBg} ${theme.iconColor} flex items-center justify-center`}
          >
            <img src={icon} alt="" className="size-12" />
          </div>
          <div className="text-right">
            <p className={`text-3xl font-medium ${theme.statColor}`}>
              {industry.stats.metric}
            </p>
            <p className="text-xs md:text-sm font-semibold text-gray-500 mt-2">
              {industry.stats.label}
            </p>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-3">
          {industry.title}
        </h3>

        {/* Description */}
        <p className="text-sm md:text-base text-gray-500 leading-relaxed mb-3">
          {industry.description}
        </p>

        {/* Stat label */}
        <div className="space-y-2 mb-6">
          {industry.benefits.map((s) => (
            <li className="flex items-center gap-2 text-sm md:text-base text-gray-500 leading-relaxed tracking-wide  ">
              <FaRegCheckCircle size={16} color="#feb501" />
              {s}
            </li>
          ))}
        </div>

        {/* Solution badges */}
        <div>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 font-semibold ml-2">
            Core Products
          </p>
          <div className="flex flex-wrap gap-1">
            {industry.solutions.map((sol) => (
              <button
                key={sol.slug}
                onClick={() => {
                  navigate(`/product/${sol.slug}`);
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                className={`text-sm md:text-base font-semibold px-3 py-1 rounded-full ${theme.badgeBg} ${theme.badgeText}`}
              >
                {sol.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function IndustriesPage() {
  const {slug}= useParams();
  const [highlight, setHighlight] = useState(null);

   
  
    useEffect(() => {
      window.scrollTo(0, 0);
       const industry = industries.find((item) => item.slug === slug);
      setHighlight(industry);
    }, [slug]);

  return (
    <>
       <div className="relative w-full h-[250px] md:h-[200px] overflow-hidden py-6">
      {/* Dark Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-slate-200 to-gray-300"></div>
      
      {/* Grid Pattern Overlay - Fixed Version */}
      <div className="absolute inset-0 opacity-30">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)"/>
        </svg>
      </div>
      
      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full sm:px-6 text-center lg:mt-10">
        <div className="w-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-jakarta font-bold text-gray-700 leading-tight">
              Industries We Serve
            </h1>
        </div>
      </div>
    </div>
      <div className="min-h-screen bg-gray-50 px-8 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {industries.map((industry) => (
          <IndustryCard
            key={industry.slug}
            industry={industry}
            highlight={highlight}
          />
        ))}
      </div>
          <div>
            <CapabilitiesSection />
          </div>
        </div>
      </div>
    </>
  );
}
